import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Chart } from 'chart.js/auto';
import { Game, GenericMedia } from '../game.model';
import { GamesService } from '../games.service';

@Component({
    selector: 'app-game-page',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        TranslateModule,
    ],
    templateUrl: './game-page.component.html',
    styleUrl: './game-page.component.css',
})
export class GamePageComponent implements OnInit, OnDestroy
{
    games: Game[] = [];
    game: Game | undefined;
    difficulties: string[] = ['Kids', 'Beginner', 'Intermediate', 'Hard', 'VeryHard'];
    currentParamKey: string | null = null;
    differences: { [difficulty: string]: string } | null = null;
    // videoError = false;
    chart: Chart | null = null;
    _errorsImagePaths: Array<string> = [];
    _errorsVideoPaths: Array<string> = [];
    _mediasWithLoadingErrorsList: GenericMedia[] = [];

    selectedGameId: string | null = null;

    @ViewChild('rdChart') rdChartCanvas!: ElementRef<HTMLCanvasElement>;

    constructor(
        private readonly gamesService: GamesService,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
    )
    {
    }

    ngOnInit(): void
    {
        this.games = this.gamesService.getGames();
        this.route.paramMap.subscribe(params =>
        {
            const gameId = params.get('gameId');
            if (gameId)
            {
                this.game = this.gamesService.getGameById(gameId);
                this.selectedGameId = this.game?.id ?? null;
                // this.videoError = false;
                this.clearDiffHighlight();
                this.updateChart();
            }
        });
    }

    ngOnDestroy(): void
    {
        this.destroyChart();
    }

    onGameChange(selectedId: string): void
    {
        if (selectedId)
        {
            this.selectedGameId = selectedId;
            this.router.navigate(['/games', selectedId]);
        }
    }

    onImageError(event: Event, baseMedia: GenericMedia | undefined = undefined): void
    {
        // this._imageErrosPaths = true;

        const imgElem = event.target as HTMLImageElement;
        this._errorsImagePaths.push(imgElem.src);

        // imgElem.src = 'https://via.placeholder.com/400x200.png?text=Image+Not+Found';
        // imgElem.classList.add('opacity-80');

        if (baseMedia && !this._mediasWithLoadingErrorsList.includes(baseMedia))
            this._mediasWithLoadingErrorsList.push(baseMedia);
    }

    onVideoError(event: Event, baseMedia: GenericMedia | undefined = undefined): void
    {
        const videoElem = event.target as HTMLImageElement;
        this._errorsVideoPaths.push(videoElem.src);

        console.log('##>> 01 | videoElem.src = ', videoElem.src);
        console.log('##>> 01 | this._errorsVideoPaths = ', this._errorsVideoPaths);

        if (baseMedia && !this._mediasWithLoadingErrorsList.includes(baseMedia))
            this._mediasWithLoadingErrorsList.push(baseMedia);
    }

    highlightDifferences(param: { key: string; values: { [difficulty: string]: string } }, baseDiff: string): void
    {
        if (!param || !param.values)
        {
            return;
        }

        this.currentParamKey = param.key;
        const baseValueStr: string = param.values[baseDiff];
        this.differences = {};
        const baseNum = this.parseNumber(baseValueStr);

        for (const diff of this.difficulties)
        {
            const valStr = param.values[diff];
            const valNum = this.parseNumber(valStr);
            if (baseNum !== null && valNum !== null)
            {
                const diffPercent = baseNum === 0 ? 0 : ((valNum - baseNum) / baseNum) * 100;
                if (Math.round(diffPercent) === 0)
                {
                    this.differences[diff] = '--';
                }
                else
                {
                    const sign = diffPercent > 0 ? '+' : '';
                    this.differences[diff] = sign + Math.round(diffPercent) + '%';
                }
            }
            else
            {
                this.differences[diff] = '--';
            }
        }
    }

    clearDiffHighlight(): void
    {
        this.currentParamKey = null;
        this.differences = null;
    }

    private parseNumber(value: string): number | null
    {
        if (!value)
        {
            return null;
        }

        let raw = value;
        raw = raw.split('?')[0];
        raw = raw.replace(/[^0-9.\-]/g, '');

        if (!raw)
        {
            return null;
        }

        const num = parseFloat(raw);
        return isNaN(num) ? null : num;
    }

    private updateChart(): void
    {
        this.destroyChart();

        if (!this.game)
        {
            return;
        }

        const rdParam = this.game.balancing?.find(p => p.key === 'roundDuration' || p.param.toLowerCase().includes('round duration'));

        if (!rdParam)
        {
            return;
        }

        const labels: string[] = this.difficulties;
        const dataPoints: number[] = [];

        for (const diff of this.difficulties)
        {
            const valStr = rdParam.values[diff];
            const numVal = this.parseNumber(valStr);
            dataPoints.push(numVal ?? 0);
        }

        const ctx = this.rdChartCanvas?.nativeElement.getContext('2d');

        if (ctx)
        {
            this.chart = new Chart(ctx, {
                type: 'line',
                data: {
                    labels,
                    datasets: [
                        {
                            label: 'Round Duration (s)',
                            data: dataPoints,
                            borderColor: '#4bc0c0',
                            backgroundColor: '#4bc0c0',
                            fill: false,
                            tension: 0.1,
                        },
                    ],
                },
                options: {
                    plugins: {
                        legend: {
                            labels: { color: '#ccc' },
                        },
                    },
                    scales: {
                        x: { ticks: { color: '#ccc' } },
                        y: { ticks: { color: '#ccc' } },
                    },
                },
            });
        }
    }

    private destroyChart(): void
    {
        if (this.chart)
        {
            this.chart.destroy();
            this.chart = null;
        }
    }
}
